import type React from "react";
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { get_event_with_claim } from "../../apis/helper";
import {
	SceneController,
	useTeamEnergy,
} from "../../components/SceneController";
import {
	connectSseThunk,
	fetchEventThunk,
	getAppendCodeThunk,
	getCheckInCodeThunk,
} from "../../redux/events/events-thunks";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
	advancePlaylist,
	kioskBatchQueuePlaybacks,
	kioskDequeuePlayback,
	setKioskStage,
	updatePlaylist,
} from "../../redux/kiosk/kiosk-action";
import {
	type DeterminedState,
	determineKioskState,
} from "../../redux/kiosk/kiosk-utils";
import { generateCheckInUrl, generateJoinUrl } from "../../redux/qr/qr-utils";
import { MainVideoPlayer } from "./components/MainVideoPlayer";
import { KioskScoreBoard } from "./components/ScoreBoard/KioskScoreBoard";
import { KioskThemeProvider } from "./context/KioskThemeContext";
import { useKioskPlaylist } from "./hooks/useKioskPlaylist";
import { useKioskStats } from "./hooks/useKioskStats";

// Lazy-loaded Screens
const IdleScreen = lazy(() =>
	import("./components/Screens/IdleScreen").then((module) => ({
		default: module.IdleScreen,
	})),
);
const WaitingScreen = lazy(() =>
	import("./components/Screens/WaitingScreen").then((module) => ({
		default: module.WaitingScreen,
	})),
);
const PlayingScreen = lazy(() =>
	import("./components/Screens/PlayingScreen").then((module) => ({
		default: module.PlayingScreen,
	})),
);
const EndgameScreen = lazy(() =>
	import("./components/Screens/EndgameScreen").then((module) => ({
		default: module.EndgameScreen,
	})),
);

const KioskPage: React.FC = () => {
	const { id: eventId } = useParams<{ id: string }>();
	const location = useLocation();
	const { t } = useTranslation();

	const dispatch = useAppDispatch();

	const event = useAppSelector((state) =>
		eventId ? state.events.events[eventId] : null,
	);

	const liveStatsUrl = useMemo(() => {
		return `${window.location.origin}/events/${eventId}`;
	}, [eventId]);

	const {
		stage,
		subStage,
		sessionIdx,
		currentPlayback,
		playlist,
		playlistIdx,
	} = useAppSelector((state) => state.kiosk);

	const appendToken = useAppSelector((state) =>
		eventId ? state.events.appendTokens[eventId] : null,
	);

	const checkInToken = useAppSelector((state) =>
		eventId ? state.events.checkInTokens[eventId] : null,
	);

	const sseConnected = useAppSelector((state) => state.events.sse_connected);

	const playerRef = useRef<HTMLVideoElement>(null);
	const replayPlayerRef = useRef<HTMLVideoElement>(null);
	const prevStage = useRef(stage);
	const countdownAudioRef = useRef<HTMLAudioElement | null>(null);
	const endgameTimerRef = useRef<number | null>(null);

	// Team Energy System
	const {
		teamA: energyA,
		teamB: teamBEnergy,
		incrementA,
		incrementB,
		resetA: resetEnergyA,
		resetB: resetEnergyB,
		resetAll: resetAllEnergy,
	} = useTeamEnergy(5);

	// QR Code and Countdown
	const [QRUrl, setQRUrl] = useState<string | null>(null);
	const [countdown, setCountdown] = useState(60);

	// Stats Display State
	const [statsTeamIndex, setStatsTeamIndex] = useState<0 | 1>(0);
	const [showJoinQr, setShowJoinQr] = useState(false);

	// Check-in State (from unified state machine)
	const [requiresCheckIn, setRequiresCheckIn] = useState(false);
	const [checkInQRUrl, setCheckInQRUrl] = useState<string | null>(null);

	// Determine Override Mode (from Hash)
	const overrideSessionIdx = useMemo(() => {
		if (location.hash) {
			const idx = parseInt(location.hash.replace("#", ""), 10);
			return Number.isNaN(idx) ? null : idx;
		}
		return null;
	}, [location.hash]);

	// Determine effective stage/session
	const displayStage = overrideSessionIdx !== null ? "endgame" : stage;
	const displaySubStage =
		overrideSessionIdx !== null ? "all_processed" : subStage;

	// Play countdown sound at 10 seconds (audio is ~10s, ends at countdown === 0)
	useEffect(() => {
		if (
			countdown === 10 &&
			(displayStage === "waiting" || displayStage === "playing")
		) {
			if (!countdownAudioRef.current) {
				countdownAudioRef.current = new Audio(
					"/heavy/ten_seconds_countdown.MP3",
				);
			}
			countdownAudioRef.current.currentTime = 0;
			countdownAudioRef.current.play().catch((e) => void e);
		}
	}, [countdown, displayStage]);

	const activeSessionIdx = useMemo(() => {
		if (overrideSessionIdx !== null) return overrideSessionIdx;
		if (sessionIdx !== undefined) return sessionIdx;
		if (event?.sessions?.length) return event.sessions.length - 1;
		return 0;
	}, [sessionIdx, event?.sessions, overrideSessionIdx]);

	// Reset energy when switching to a new session
	const prevSessionIdx = useRef(activeSessionIdx);
	useEffect(() => {
		if (prevSessionIdx.current !== activeSessionIdx) {
			resetAllEnergy();
			fireCountRef.current = [0, 0];
			prevSessionIdx.current = activeSessionIdx;
		}
	}, [activeSessionIdx, resetAllEnergy]);

	const hasNextGame = useMemo(() => {
		if (!event?.sessions) return false;
		return activeSessionIdx < event.sessions.length - 1;
	}, [event, activeSessionIdx]);

	const [isEventEnded, setIsEventEnded] = useState(false);

	const { targetQrUrl, targetQrLabel, targetQrSubLabel, showStatus } =
		useMemo(() => {
			if (displayStage !== "endgame") return {};

			const GAME_DURATION_MS = 12 * 60 * 1000;
			const now = Date.now();
			const eventEndTime = event?.ended_at
				? new Date(event.ended_at).getTime()
				: 0;
			const hasEnoughTime = eventEndTime - now > GAME_DURATION_MS;

			// Show append QR if game is processed OR (processing AND no next game) AND event hasn't ended
			const canShowAppend = !hasNextGame && !isEventEnded && hasEnoughTime;

			// Only show append/join QR if we have a valid tokenized URL.
			// Otherwise, stay on stats to avoid confusing users with a generic event page QR.
			if (canShowAppend && showJoinQr && QRUrl) {
				return {
					targetQrUrl: QRUrl,
					targetQrLabel: t("kiosk.endgame.join_game"),
					targetQrSubLabel: t("scan_to_join_next"),
					showStatus: false,
				};
			}

			return {
				targetQrUrl: liveStatsUrl,
				targetQrLabel: t("kiosk.endgame.live_stats_video"),
				targetQrSubLabel: t("kiosk.endgame.scan_for_details"),
				showStatus: true,
			};
		}, [
			displayStage,
			hasNextGame,
			QRUrl,
			liveStatsUrl,
			showJoinQr,
			isEventEnded,
			t,
			event?.ended_at,
		]);

	// --- Playlist Logic ---
	const playlistItems = useKioskPlaylist(
		displayStage,
		displaySubStage,
		activeSessionIdx,
		event,
	);

	// URL-only list for Redux; team lookup map for label rendering
	const playlistUrls = useMemo(
		() => playlistItems.map((item) => item.url),
		[playlistItems],
	);
	const playlistTeamMap = useMemo(() => {
		const map = new Map<string, number | null>();
		for (const item of playlistItems) {
			if (item.teamId != null) map.set(item.url, item.teamId);
		}
		return map;
	}, [playlistItems]);

	useEffect(() => {
		const stageChanged = prevStage.current !== displayStage;
		prevStage.current = displayStage;

		dispatch(
			updatePlaylist({
				playlist: playlistUrls,
				resetIndex: stageChanged,
			}),
		);
	}, [playlistUrls, displayStage, dispatch]);

	// Reset stats/QR state when leaving endgame
	useEffect(() => {
		if (displayStage !== "endgame") {
			setStatsTeamIndex(0);
			setShowJoinQr(false);
			endgameTimerRef.current = null;
		}
	}, [displayStage]);

	// Video Source logic
	const playlistVideoUrl = useMemo(() => {
		if (playlist.length > 0) {
			return playlist[playlistIdx] || playlist[0];
		}
		return null;
	}, [playlist, playlistIdx]);

	const replayVideoUrl = useMemo(() => {
		// If we are in override mode (focusing on a specific game),
		// ignore replays that belong to other sessions.
		if (
			overrideSessionIdx !== null &&
			currentPlayback &&
			currentPlayback.session_idx !== overrideSessionIdx
		) {
			return null;
		}
		return currentPlayback?.replay_url || null;
	}, [currentPlayback, overrideSessionIdx]);

	// Resolve team info for the currently playing playlist video
	const playlistTeamId = useMemo(() => {
		if (!playlistVideoUrl) return null;
		return playlistTeamMap.get(playlistVideoUrl) ?? null;
	}, [playlistVideoUrl, playlistTeamMap]);

	// Unified logic for single-player screens
	const currentVideoUrl = useMemo(() => {
		return replayVideoUrl || playlistVideoUrl;
	}, [replayVideoUrl, playlistVideoUrl]);

	const handlePlaylistVideoEnded = () => {
		const nextIdx =
			playlist.length > 0 ? (playlistIdx + 1) % playlist.length : 0;
		dispatch(advancePlaylist());
		// If the next video URL is the same (single-item playlist or duplicate URL),
		// the src prop won't change so React won't re-render. Manually restart.
		if (playlist[nextIdx] === playlist[playlistIdx] && playerRef.current) {
			playerRef.current.currentTime = 0;
			playerRef.current.play();
		}
	};

	const replayCountRef = useRef(0);
	const replayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const replayStallRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const lastTimeUpdateRef = useRef(0);

	const clearReplayTimers = () => {
		if (replayTimeoutRef.current) {
			clearTimeout(replayTimeoutRef.current);
			replayTimeoutRef.current = null;
		}
		if (replayStallRef.current) {
			clearInterval(replayStallRef.current);
			replayStallRef.current = null;
		}
	};

	// Reset replay count and start fallback timers when a new playback starts
	const currentPlaybackId = currentPlayback?.replay_url;
	useEffect(() => {
		replayCountRef.current = 0;
		lastTimeUpdateRef.current = Date.now();
		clearReplayTimers();

		if (currentPlaybackId) {
			console.debug("🎬 Replay started:", currentPlaybackId);

			// Hard fallback: force-dequeue after 20s no matter what
			replayTimeoutRef.current = setTimeout(() => {
				console.warn("⚠️ Replay timeout — force-dequeuing stuck playback", {
					url: currentPlaybackId,
					replayCount: replayCountRef.current,
					triggerAnimTeam: triggerAnimTeamRef.current,
					displaySubStage,
				});
				clearReplayTimers();
				dispatch(kioskDequeuePlayback());
			}, 20000);

			// Stall detector: if video makes no progress for 5s, skip it
			replayStallRef.current = setInterval(() => {
				const elapsed = Date.now() - lastTimeUpdateRef.current;
				if (elapsed > 5000) {
					console.warn("⚠️ Replay stalled — no timeupdate for 5s, skipping", {
						url: currentPlaybackId,
						replayCount: replayCountRef.current,
					});
					clearReplayTimers();
					dispatch(kioskDequeuePlayback());
				}
			}, 2000);
		}

		return clearReplayTimers;
	}, [currentPlaybackId, dispatch]);

	const handleReplayTimeUpdate = () => {
		lastTimeUpdateRef.current = Date.now();
	};

	const handleReplayVideoEnded = () => {
		replayCountRef.current += 1;
		console.debug("🎬 Replay onEnded", {
			count: replayCountRef.current,
			url: currentPlaybackId,
			triggerAnimTeam: triggerAnimTeamRef.current,
			displaySubStage,
		});

		if (replayCountRef.current < 2 && replayPlayerRef.current) {
			// HLS streams may fail to seek to 0 — use load() to restart cleanly
			try {
				replayPlayerRef.current.currentTime = 0;
				const playPromise = replayPlayerRef.current.play();
				if (playPromise) {
					playPromise.catch((err) => {
						console.warn("⚠️ Replay second play failed:", err);
						clearReplayTimers();
						dispatch(kioskDequeuePlayback());
					});
				}
			} catch (err) {
				console.warn("⚠️ Replay seek/play threw:", err);
				clearReplayTimers();
				dispatch(kioskDequeuePlayback());
			}
			return;
		}

		clearReplayTimers();

		// If trigger_anim is active, this 2nd-play end drives the exit
		if (displaySubStage === "trigger_anim") {
			if (triggerAnimTeamRef.current === 0) {
				resetEnergyA();
			} else if (triggerAnimTeamRef.current === 1) {
				resetEnergyB();
			}
			triggerAnimTeamRef.current = null;
			dispatch(setKioskStage({ stage: "playing", subStage: "regular" }));
			dispatch(kioskDequeuePlayback());
			return;
		}

		// If trigger_anim is pending but subStage hasn't transitioned yet,
		// don't hold indefinitely — dequeue after the 2nd play
		if (triggerAnimTeamRef.current !== null) {
			console.debug(
				"🎬 Replay ended with pending triggerAnim, dequeuing anyway",
			);
			dispatch(kioskDequeuePlayback());
			return;
		}

		dispatch(kioskDequeuePlayback());
	};

	const handleMainVideoEnded = () => {
		if (currentPlayback) {
			handleReplayVideoEnded();
			return;
		}

		// Endgame highlight completion detection:
		// Start the 30s stats countdown only after all highlights have video_url
		// AND the last one finishes playing.
		if (
			displayStage === "endgame" &&
			displaySubStage === "all_processed" &&
			!endgameTimerRef.current
		) {
			const session = event?.sessions?.[activeSessionIdx];
			const highlights = session?.highlights || [];
			const allReady =
				highlights.length > 0 && highlights.every((h) => !!h.video_url);
			const isLastItem = playlistIdx === playlist.length - 1;

			if (highlights.length === 0) {
				// No highlights at all — start timer immediately
				endgameTimerRef.current = Date.now() + 30_000;
			} else if (allReady && isLastItem) {
				// All highlights delivered and last one just finished playing
				endgameTimerRef.current = Date.now() + 30_000;
			}
		}

		handlePlaylistVideoEnded();
	};

	// Track which team triggered the 3D animation so we can reset its energy after
	const triggerAnimTeamRef = useRef<0 | 1 | null>(null);

	// Track how many times each team reached "on fire" (trigger_anim)
	const fireCountRef = useRef<[number, number]>([0, 0]);
	// Trigger 3D animation after new_shot if scored team's energy is full.
	// Guard on `stage` so the timeout is cancelled if we leave "playing"
	// (e.g. session ends → endgame before the 2s delay fires).
	useEffect(() => {
		if (!currentPlayback) return;
		if (stage !== "playing") return;

		const teamId = Number(currentPlayback.result?.team_id);
		const energy = teamId === 0 ? energyA.progress : teamBEnergy.progress;

		if (energy >= 100) {
			const idx = teamId === 0 ? 0 : 1;
			triggerAnimTeamRef.current = idx;
			fireCountRef.current[idx]++;
			const timer = setTimeout(() => {
				dispatch(
					setKioskStage({
						stage: "playing",
						subStage: "trigger_anim",
					}),
				);
			}, 2000);
			return () => clearTimeout(timer);
		}
	}, [
		currentPlayback,
		energyA.progress,
		teamBEnergy.progress,
		dispatch,
		stage,
	]);

	// Reset replay count when entering trigger_anim so the replay plays twice again
	useEffect(() => {
		if (displaySubStage === "trigger_anim") {
			replayCountRef.current = 0;
			if (replayPlayerRef.current) {
				replayPlayerRef.current.currentTime = 0;
				replayPlayerRef.current.play();
			}
		}
	}, [displaySubStage]);

	// Fallback timeout: escape trigger_anim if replay doesn't finish in 15s
	useEffect(() => {
		if (displaySubStage !== "trigger_anim") return;

		const fallback = setTimeout(() => {
			if (triggerAnimTeamRef.current === 0) {
				resetEnergyA();
			} else if (triggerAnimTeamRef.current === 1) {
				resetEnergyB();
			}
			triggerAnimTeamRef.current = null;
			dispatch(
				setKioskStage({
					stage: "playing",
					subStage: "regular",
				}),
			);
			dispatch(kioskDequeuePlayback());
		}, 15000);

		return () => clearTimeout(fallback);
	}, [displaySubStage, dispatch, resetEnergyA, resetEnergyB]);

	// --- End Playlist Logic ---

	// Endgame reveal sequence: processing → reveal (anim plays 2×, then overlay stats) → all_processed
	// Track whether the reveal sequence has been completed for the current session.
	const endgameRevealDoneRef = useRef(false);
	// Track whether the 3D animation has finished (played twice) so we can show RevealStatsBoard overlay
	const [showRevealStats, setShowRevealStats] = useState(false);

	// Reset reveal tracking when we leave endgame
	useEffect(() => {
		if (stage !== "endgame") {
			endgameRevealDoneRef.current = false;
			setShowRevealStats(false);
		}
	}, [stage]);

	// Intercept all_processed → reveal when reveal hasn't been shown yet.
	// Catches both SSE (processing_done) and heartbeat transitions.
	useEffect(() => {
		if (
			displayStage === "endgame" &&
			displaySubStage === "all_processed" &&
			!endgameRevealDoneRef.current
		) {
			dispatch(setKioskStage({ stage: "endgame", subStage: "reveal" }));
		}
	}, [displayStage, displaySubStage, dispatch]);

	// Auto-transition from reveal → all_processed 20 seconds after stats overlay appears
	useEffect(() => {
		if (displaySubStage !== "reveal" || !showRevealStats) return;

		const timer = setTimeout(() => {
			endgameRevealDoneRef.current = true;
			dispatch(
				setKioskStage({
					stage: "endgame",
					subStage: "all_processed",
				}),
			);
		}, 20000);

		return () => clearTimeout(timer);
	}, [displaySubStage, showRevealStats, dispatch]);

	// Fallback: if 3D animation doesn't finish within 15s, force show stats overlay
	useEffect(() => {
		if (displaySubStage !== "reveal" || showRevealStats) return;

		const fallback = setTimeout(() => {
			setShowRevealStats(true);
		}, 15000);

		return () => clearTimeout(fallback);
	}, [displaySubStage, showRevealStats]);

	const tickRef = useRef(0);

	// Heartbeat to update Kiosk State and Toggles
	useEffect(() => {
		if (!event) return;

		const interval = setInterval(() => {
			tickRef.current += 1;

			// 1. Determine Kiosk State (only if not overridden)
			if (overrideSessionIdx === null) {
				const determined: DeterminedState = determineKioskState(event);

				// Skip heartbeat override during transient playback subStages,
				// but only while still in the playing stage. If the event has
				// moved to endgame (or any other stage), force the transition.
				const isPlaybackSubStage =
					(subStage === "new_shot" || subStage === "trigger_anim") &&
					determined.stage === "playing";

				// Guard: prevent leaving endgame until highlight timer expires
				let shouldSkipStageDispatch = false;
				const wantsToLeaveEndgame =
					stage === "endgame" && determined.stage !== "endgame";

				if (wantsToLeaveEndgame) {
					// PRIORITY: Always transition if next game is imminent or already started.
					// Players need to see the countdown / active game — highlights are not worth blocking it.
					const nextGameImminent =
						determined.stage === "waiting" &&
						determined.subStage === "imminent";
					const nextGameStarted = determined.stage === "playing";

					if (nextGameImminent || nextGameStarted) {
						// Force transition, abandon highlights
						endgameTimerRef.current = null;
						// fall through to normal dispatch below
					} else if (
						!endgameTimerRef.current ||
						Date.now() < endgameTimerRef.current
					) {
						// Timer not started or not expired — stay in endgame.
						// Skip the setKioskStage dispatch but continue the rest of the heartbeat.
						shouldSkipStageDispatch = true;
					} else {
						// Timer expired — allow the transition, clean up
						endgameTimerRef.current = null;
					}
				}

				// Guard: don't override reveal with heartbeat
				const isRevealSubStage = stage === "endgame" && subStage === "reveal";

				// Intercept: if heartbeat says all_processed but reveal not done, start reveal
				let effectiveSubStage = determined.subStage;
				if (
					determined.stage === "endgame" &&
					determined.subStage === "all_processed" &&
					!endgameRevealDoneRef.current &&
					subStage === "processing"
				) {
					effectiveSubStage = "reveal";
				}

				if (
					!shouldSkipStageDispatch &&
					!isPlaybackSubStage &&
					!isRevealSubStage &&
					(determined.stage !== stage ||
						effectiveSubStage !== subStage ||
						determined.sessionIdx !== sessionIdx)
				) {
					dispatch(
						setKioskStage({
							stage: determined.stage,
							subStage: effectiveSubStage ?? undefined,
							sessionIdx: determined.sessionIdx,
						}),
					);
				}

				if (determined.secondsLeft !== undefined) {
					setCountdown(determined.secondsLeft);
				} else {
					setCountdown(0);
				}

				// Update check-in requirement state
				if (determined.requiresCheckIn !== undefined) {
					setRequiresCheckIn(determined.requiresCheckIn);
				}

				// Update event ended state (time-reactive)
				if (determined.isEventEnded !== undefined) {
					setIsEventEnded(determined.isEventEnded);
				}
			}

			// 2. Start endgame timer if in all_processed but no highlights to wait for.
			//    handleMainVideoEnded normally starts the timer, but if no video is
			//    playing (empty playlist / no highlights), it never fires.
			if (
				displayStage === "endgame" &&
				displaySubStage === "all_processed" &&
				!endgameTimerRef.current
			) {
				const session = event?.sessions?.[activeSessionIdx];
				const highlights = session?.highlights || [];
				if (highlights.length === 0) {
					endgameTimerRef.current = Date.now() + 30_000;
				}
			}

			// 3. Endgame Toggle (every 10 seconds)
			if (displayStage === "endgame") {
				if (tickRef.current % 10 === 0) {
					setStatsTeamIndex((prev) => (prev === 0 ? 1 : 0));
					setShowJoinQr((prev) => !prev);
				}
			}

			// 4. Token Refresh Logic (every 30 seconds)
			if (tickRef.current % 30 === 0 && eventId && !isEventEnded) {
				const validateToken = async () => {
					if (appendToken) {
						try {
							await get_event_with_claim(eventId, appendToken);
						} catch (_err) {
							dispatch(getAppendCodeThunk(eventId));
						}
					} else {
						dispatch(getAppendCodeThunk(eventId));
					}
				};
				validateToken();
			}
		}, 1000);

		return () => clearInterval(interval);
	}, [
		event,
		stage,
		subStage,
		dispatch,
		overrideSessionIdx,
		displayStage,
		displaySubStage,
		activeSessionIdx,
		appendToken,
		eventId,
		isEventEnded,
		sessionIdx,
	]);

	// lazy init appendToken
	useEffect(() => {
		if (eventId) {
			if (appendToken) {
				const redirect_url = generateJoinUrl(eventId, appendToken);
				setQRUrl(redirect_url);
			} else {
				dispatch(getAppendCodeThunk(eventId));
			}
		}
	}, [eventId, appendToken, dispatch]);

	// lazy init checkInToken (when check-in is required)
	useEffect(() => {
		if (eventId && requiresCheckIn) {
			if (checkInToken) {
				const checkInUrl = generateCheckInUrl(eventId, checkInToken);
				setCheckInQRUrl(checkInUrl);
			} else {
				dispatch(getCheckInCodeThunk(eventId));
			}
		}
	}, [eventId, checkInToken, requiresCheckIn, dispatch]);

	// Initial Fetch & SSE Connection
	useEffect(() => {
		if (eventId) {
			const stream = dispatch(fetchEventThunk(eventId));
			dispatch(connectSseThunk(eventId));

			return () => {
				stream.abort();
			};
		}
	}, [eventId, dispatch]);

	// Restore highlight queue on page reload for non-playing stages.
	// During playing, SSE delivers new events live — restoring old replays
	// would disrupt the live feed. For endgame/waiting/idle, restored
	// highlights serve as background content via the main video player.
	const queueRestoredRef = useRef(false);

	useEffect(() => {
		if (queueRestoredRef.current) return;
		if (!event?.sessions?.length) return;
		if (stage === "playing") return;

		// Find the last session that has scoring highlights.
		// During endgame/idle, activeSessionIdx is usually correct.
		// During waiting, it points to the next session (no results),
		// so we search backwards from the end.
		let targetIdx = -1;
		for (let i = event.sessions.length - 1; i >= 0; i--) {
			const s = event.sessions[i];
			if (s.result_details?.length) {
				targetIdx = i;
				break;
			}
		}
		if (targetIdx === -1) return;

		const session = event.sessions[targetIdx];
		const scoringPlays = (session.result_details as any[])
			.map((result: any, idx: number) => ({ result, idx }))
			.filter(
				({ result }: { result: any }) =>
					result.field_goal_result === "score" &&
					result.replay_url &&
					result.team_id != null,
			)
			.slice(-5)
			.map(({ result, idx }: { result: any; idx: number }) => ({
				session_idx: targetIdx,
				result_idx: idx,
				replay_url: result.replay_url,
				result,
			}));

		if (scoringPlays.length > 0) {
			dispatch(kioskBatchQueuePlaybacks(scoringPlays));
		}

		queueRestoredRef.current = true;
	}, [event, stage, dispatch]);

	// Derive team names from session.team_mapping (API) with fallback
	const teamNames = useMemo<[string, string]>(() => {
		const session = event?.sessions?.[activeSessionIdx];
		const mapping = session?.team_mapping;
		return [mapping?.["0"] || "Team A", mapping?.["1"] || "Team B"];
	}, [event, activeSessionIdx]);

	const teamIconSrcs = useMemo<[string, string]>(
		() => ["/img/event-page/team_light.svg", "/img/event-page/team_dark.svg"],
		[],
	);

	const teamIcons = useMemo<[React.ReactNode, React.ReactNode]>(
		() => [
			<div
				key="team-a"
				className="flex w-[60px] h-[60px] items-center justify-center rounded-full border border-white/20 bg-kiosk-surface-secondary"
			>
				<img
					src={teamIconSrcs[0]}
					alt={teamNames[0]}
					className="w-[60px] h-[60px]"
				/>
			</div>,
			<div
				key="team-b"
				className="flex w-[60px] h-[60px] items-center justify-center rounded-full border border-white/20 bg-kiosk-surface-secondary"
			>
				<img
					src={teamIconSrcs[1]}
					alt={teamNames[1]}
					className="w-[60px] h-[60px]"
				/>
			</div>,
		],
		[teamNames, teamIconSrcs],
	);

	const { teamStats, teamScores, getDetailedTeamStats, getTeamShots } =
		useKioskStats(event, activeSessionIdx, teamIcons, teamNames);

	// Sync energy with scores
	const prevScores = useRef(teamScores);
	useEffect(() => {
		if (teamScores.team0 > prevScores.current.team0) {
			incrementA(1);
		}
		if (teamScores.team1 > prevScores.current.team1) {
			incrementB(1);
		}
		prevScores.current = teamScores;
	}, [teamScores, incrementA, incrementB]);

	const currentTeamStats = useMemo(() => {
		// Use live-tracked fire count if available; otherwise compute from
		// shot history (covers page reload and Storybook).
		let fireCount = fireCountRef.current[statsTeamIndex];
		if (fireCount === 0) {
			const session = event?.sessions?.[activeSessionIdx];
			const details = session?.result_details;
			if (details && details.length > 0) {
				let streak = 0;
				for (const shot of details) {
					const idx = Number(shot.team_id) === 0 ? 0 : 1;
					if (idx !== statsTeamIndex) continue;
					if (shot.field_goal_result === "score") {
						streak++;
						if (streak >= 5) {
							fireCount++;
							streak = 0;
						}
					} else {
						streak = 0;
					}
				}
			}
		}
		return {
			...getDetailedTeamStats(statsTeamIndex),
			fireCount,
		};
	}, [statsTeamIndex, getDetailedTeamStats, event, activeSessionIdx]);

	const teamShots = useMemo(() => {
		return getTeamShots(statsTeamIndex);
	}, [statsTeamIndex, getTeamShots]);

	const bothTeamStats = useMemo(
		() => ({
			home: getDetailedTeamStats(0),
			away: getDetailedTeamStats(1),
		}),
		[getDetailedTeamStats],
	);

	// Clear scores when not in playing or endgame stages
	const showScores = displayStage === "playing" || displayStage === "endgame";

	const homeTeam = useMemo(
		() => ({
			name: teamNames[0],
			score: showScores ? teamScores.team0 : 0,
			icon: teamIcons[0],
			isWinner: teamScores.team0 > teamScores.team1,
			energy: energyA.progress,
			stats: {
				label: "FG",
				value: showScores
					? `${teamStats[0].made} / ${teamStats[0].total}`
					: "0 / 0",
				progress:
					showScores && teamStats[0].total > 0
						? (teamStats[0].made / teamStats[0].total) * 100
						: 0,
			},
		}),
		[
			teamScores.team0,
			teamScores.team1,
			energyA.progress,
			teamStats,
			showScores,
			teamIcons,
			teamNames,
		],
	);

	const awayTeam = useMemo(
		() => ({
			name: teamNames[1],
			score: showScores ? teamScores.team1 : 0,
			icon: teamIcons[1],
			isWinner: teamScores.team1 > teamScores.team0,
			energy: teamBEnergy.progress,
			stats: {
				label: "FG",
				value: showScores
					? `${teamStats[1].made} / ${teamStats[1].total}`
					: "0 / 0",
				progress:
					showScores && teamStats[1].total > 0
						? (teamStats[1].made / teamStats[1].total) * 100
						: 0,
			},
		}),
		[
			teamScores.team0,
			teamScores.team1,
			teamBEnergy.progress,
			teamStats,
			showScores,
			teamIcons,
			teamNames,
		],
	);

	const formatTime = (seconds: number) => {
		const m = Math.floor(seconds / 60);
		const s = seconds % 60;
		return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
	};

	const scoreBoard = (
		<KioskScoreBoard
			homeTeam={homeTeam}
			awayTeam={awayTeam}
			time={displayStage === "endgame" ? "00:00" : formatTime(countdown)}
			isProcessing={
				displayStage === "endgame" && displaySubStage === "processing"
			}
			gameState={
				displayStage === "playing"
					? displaySubStage === "trigger_anim"
						? "trigger_anim"
						: currentPlayback
							? "new_shot"
							: "regular"
					: undefined
			}
		/>
	);

	if (!event) {
		return <div>{t("common.loading_event")}</div>;
	}

	const renderContent = () => {
		// joinQrUrl is ONLY for the tokenized append flow.
		// If missing, we hide the QR panel in components that use it (e.g. PlayingScreen).
		const joinQrUrl = !isEventEnded && QRUrl ? QRUrl : undefined;

		switch (displayStage) {
			case "idle": {
				const effectiveIdleQr = isEventEnded
					? liveStatsUrl
					: joinQrUrl || liveStatsUrl;
				const showJoinInstruction = !isEventEnded && !!joinQrUrl;

				return (
					<IdleScreen
						qrCodeUrl={effectiveIdleQr}
						arenaName={event?.location}
						title={
							isEventEnded
								? t("kiosk.idle.event_ended")
								: t("kiosk.idle.ready_to_play")
						}
						subtitle={
							isEventEnded
								? t("kiosk.idle.thank_you")
								: t("kiosk.idle.start_now")
						}
						instruction={
							isEventEnded || !showJoinInstruction
								? t("kiosk.endgame.scan_for_details")
								: t("kiosk.idle.scan_to_start")
						}
						promoContent={
							<MainVideoPlayer
								currentVideoUrl={currentVideoUrl}
								displayStage={displayStage}
								handleMainVideoEnded={handleMainVideoEnded}
								playerRef={playerRef}
							/>
						}
						replayLabel={
							currentPlayback
								? currentPlayback.result.field_goal_position === "3PFG"
									? t("kiosk.playing.three_points")
									: t("kiosk.playing.two_points")
								: undefined
						}
						replayTeamIcon={
							currentPlayback
								? teamIconSrcs[
										Number(currentPlayback.result.team_id) === 0 ? 0 : 1
									]
								: undefined
						}
						replayTeamName={
							currentPlayback
								? teamNames[
										Number(currentPlayback.result.team_id) === 0 ? 0 : 1
									]
								: undefined
						}
					/>
				);
			}
			case "waiting": {
				// Determine waiting screen mode based on check-in requirement and timing
				const waitingMode = requiresCheckIn
					? "checkin"
					: displaySubStage === "imminent"
						? "imminent"
						: "normal";

				const nextGameTitle = event?.sessions?.[activeSessionIdx]?.name;
				const nextSessionStart =
					event?.sessions?.[activeSessionIdx]?.started_at;
				const nextGameTime = nextSessionStart
					? new Date(nextSessionStart).toLocaleTimeString([], {
							hour: "2-digit",
							minute: "2-digit",
							hour12: false,
						})
					: "--:--";

				return (
					<WaitingScreen
						mode={waitingMode}
						nextGameTitle={nextGameTitle}
						nextGameTime={nextGameTime}
						secondsLeft={countdown}
						eventUrl={joinQrUrl || liveStatsUrl}
						checkInUrl={
							requiresCheckIn ? (checkInQRUrl ?? undefined) : undefined
						}
						promoContent={
							<MainVideoPlayer
								currentVideoUrl={currentVideoUrl}
								displayStage={displayStage}
								handleMainVideoEnded={handleMainVideoEnded}
								playerRef={playerRef}
							/>
						}
						videoBackground={
							<MainVideoPlayer
								currentVideoUrl={currentVideoUrl}
								displayStage={displayStage}
								handleMainVideoEnded={handleMainVideoEnded}
								playerRef={playerRef}
								className="w-full h-full object-contain opacity-60"
							/>
						}
						scoreBoardContent={scoreBoard}
					/>
				);
			}
			case "playing": {
				// Determine game state based on subStage and current playback.
				// "ending" takes highest priority — ignore replays and animations.
				const playingGameState =
					displaySubStage === "ending"
						? "ending"
						: displaySubStage === "trigger_anim"
							? "trigger_anim"
							: currentPlayback
								? "new_shot"
								: "regular";

				return (
					<PlayingScreen
						gameState={playingGameState}
						videoFeed={
							<MainVideoPlayer
								currentVideoUrl={playlistVideoUrl}
								displayStage={displayStage}
								gameState={playingGameState}
								handleMainVideoEnded={handlePlaylistVideoEnded}
								playerRef={playerRef}
								label="live"
							/>
						}
						replayFeed={
							currentPlayback ? (
								<MainVideoPlayer
									currentVideoUrl={replayVideoUrl}
									displayStage={displayStage}
									gameState={playingGameState}
									handleMainVideoEnded={handleReplayVideoEnded}
									handleVideoError={() => {
										console.warn("⚠️ Replay video error — skipping playback");
										clearReplayTimers();
										dispatch(kioskDequeuePlayback());
									}}
									onTimeUpdate={handleReplayTimeUpdate}
									playerRef={replayPlayerRef}
									label="replay"
									replayTeamIcon={
										teamIconSrcs[
											Number(currentPlayback.result.team_id) === 0 ? 0 : 1
										]
									}
									replayTeamName={
										teamNames[
											Number(currentPlayback.result.team_id) === 0 ? 0 : 1
										]
									}
								/>
							) : undefined
						}
						animationContent={
							displaySubStage === "trigger_anim" ? (
								<SceneController
									teamAEnergy={energyA.progress}
									teamBEnergy={teamBEnergy.progress}
								/>
							) : null
						}
						scoreBoardContent={scoreBoard}
						arenaName={event?.location || ""}
						hostName={event?.booker || "Host"}
						countDown={displaySubStage === "ending" ? countdown : undefined}
						registerUrl={joinQrUrl}
						liveStatsUrl={liveStatsUrl}
						scoredPoints={
							currentPlayback
								? currentPlayback.result.field_goal_position === "3PFG"
									? 3
									: 2
								: undefined
						}
						scoredTeamIcon={
							currentPlayback
								? teamIconSrcs[
										Number(currentPlayback.result.team_id) === 0 ? 0 : 1
									]
								: undefined
						}
						scoredTeamName={
							currentPlayback
								? teamNames[
										Number(currentPlayback.result.team_id) === 0 ? 0 : 1
									]
								: undefined
						}
					/>
				);
			}
			case "endgame": {
				const endgameMode = (() => {
					if (overrideSessionIdx !== null) return "stats" as const;
					switch (displaySubStage) {
						case "all_processed":
							return "stats" as const;
						case "reveal":
							return "reveal" as const;
						default:
							return "processing" as const;
					}
				})();

				return (
					<EndgameScreen
						mode={endgameMode}
						scoreBoardContent={scoreBoard}
						animationContent={
							displaySubStage === "reveal" ? (
								<SceneController
									teamAEnergy={100}
									teamBEnergy={100}
									isEndgame={true}
									loop={showRevealStats}
									onAnimationFinished={() => {
										setShowRevealStats(true);
									}}
								/>
							) : null
						}
						showRevealStats={showRevealStats}
						highlightFeed={
							<MainVideoPlayer
								currentVideoUrl={currentVideoUrl}
								displayStage={displayStage}
								handleMainVideoEnded={handleMainVideoEnded}
								playerRef={playerRef}
								label="replay"
								replayTeamIcon={
									currentPlayback
										? teamIconSrcs[
												Number(currentPlayback.result.team_id) === 0 ? 0 : 1
											]
										: playlistTeamId != null
											? teamIconSrcs[playlistTeamId === 0 ? 0 : 1]
											: teamIconSrcs[statsTeamIndex]
								}
								replayTeamName={
									currentPlayback
										? teamNames[
												Number(currentPlayback.result.team_id) === 0 ? 0 : 1
											]
										: playlistTeamId != null
											? teamNames[playlistTeamId === 0 ? 0 : 1]
											: teamNames[statsTeamIndex]
								}
							/>
						}
						statsData={currentTeamStats}
						bothTeamStats={bothTeamStats}
						shots={teamShots}
						liveStatsUrl={liveStatsUrl}
						registerUrl={joinQrUrl}
						qrUrl={targetQrUrl}
						qrLabel={targetQrLabel}
						qrSubLabel={targetQrSubLabel}
						showStatus={showStatus}
						isLive={sseConnected}
						hostName={event?.booker || "Host"}
					/>
				);
			}
			default:
				return <div>Unsupported stage: {displayStage}</div>;
		}
	};

	return (
		<KioskThemeProvider>
			<div className="w-full h-full overflow-hidden bg-black text-white">
				<Suspense fallback={<div>Loading...</div>}>{renderContent()}</Suspense>
			</div>
		</KioskThemeProvider>
	);
};

export default KioskPage;
